/**
 * Offline Observation Queue (v1.1.29)
 * ====================================
 * Separate IndexedDB store for queueing facility-wide observations created
 * offline. These are NOT tied to assets — see backend/models/observation.py.
 *
 * DESIGN INVARIANT (per product owner directive 2026-02-06):
 *   This module is COMPLETELY INDEPENDENT from:
 *     - services/offlineStorage.js (legacy new-asset queue)
 *     - services/offlineExistingAssetQueue.js (v1.1.25 add-photos queue)
 *   Different database name, different store names, different sync worker.
 *   If this entire file is deleted, both existing offline flows continue
 *   to work byte-for-byte identically.
 *
 * Storage Structure:
 *   - Database: fmiq_offline_observations_db
 *   - Store:    pending_observations
 *
 * Record shape:
 *   {
 *     id: <autoincrement>,
 *     offline_observation_key: <client-generated UUID, used by server
 *                              for idempotency on retry>,
 *     assessment_id, title, description, category, urgency,
 *     estimated_cost, cost_basis_notes, recommendation,
 *     floor_level, room_number,
 *     photos_base64: [<data URI>, ...],
 *     queued_at: <ISO timestamp>,
 *     status: 'pending' | 'syncing' | 'completed' | 'failed',
 *     retry_count: <int>,
 *     last_error: <string|null>,
 *   }
 *
 * The sync worker that drains this queue lives in
 * services/offlineObservationSync.js.
 */

const DB_NAME = 'fmiq_offline_observations_db';
const DB_VERSION = 1;
const OPS_STORE = 'pending_observations';

let dbInstance = null;
let dbFailed = false;

export const initObservationDB = () => {
  return new Promise((resolve, reject) => {
    if (dbFailed) {
      reject(new Error('Observation offline DB previously failed; not retrying this session'));
      return;
    }
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    try {
      if (!window.indexedDB) {
        dbFailed = true;
        reject(new Error('IndexedDB not available'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);
      const timeout = setTimeout(() => {
        dbFailed = true;
        reject(new Error('Observation offline DB open timed out'));
      }, 5000);

      request.onerror = () => {
        clearTimeout(timeout);
        dbFailed = true;
        reject(request.error);
      };
      request.onblocked = () => {
        clearTimeout(timeout);
        dbFailed = true;
        reject(new Error('Observation offline DB blocked by another connection'));
      };
      request.onsuccess = () => {
        clearTimeout(timeout);
        dbInstance = request.result;
        dbInstance.onclose = () => { dbInstance = null; };
        dbInstance.onversionchange = () => {
          dbInstance.close();
          dbInstance = null;
        };
        resolve(dbInstance);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(OPS_STORE)) {
          const store = db.createObjectStore(OPS_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('assessment_id', 'assessment_id', { unique: false });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('offline_observation_key', 'offline_observation_key', { unique: true });
        }
      };
    } catch (err) {
      dbFailed = true;
      reject(err);
    }
  });
};

/**
 * Enqueue a facility-wide observation for later sync.
 * Returns the assigned record id on success.
 */
export const enqueueObservation = async (observationPayload) => {
  if (!observationPayload?.assessment_id) {
    throw new Error('assessment_id is required');
  }
  if (!observationPayload?.title) {
    throw new Error('title is required');
  }

  // Honor the same storage quota guard the existing-asset queue uses so
  // observations don't starve the existing queues.
  try {
    const { checkCanStore } = await import('../lib/storageManager');
    const result = await checkCanStore();
    if (result && result.canStore === false) {
      const err = new Error(result.message || 'Browser storage is nearly full');
      err.code = 'STORAGE_FULL';
      throw err;
    }
  } catch (storageErr) {
    if (storageErr?.code === 'STORAGE_FULL') {
      throw storageErr;
    }
  }

  const db = await initObservationDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const record = {
      offline_observation_key:
        observationPayload.offline_observation_key
        || `obs_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      assessment_id: observationPayload.assessment_id,
      title: observationPayload.title,
      description: observationPayload.description || '',
      category: observationPayload.category || 'Other',
      urgency: observationPayload.urgency || 'Short-term',
      estimated_cost: Number(observationPayload.estimated_cost) || 0,
      cost_basis_notes: observationPayload.cost_basis_notes || '',
      recommendation: observationPayload.recommendation || '',
      floor_level: observationPayload.floor_level || '',
      room_number: observationPayload.room_number || '',
      // v1.2.101 — Silently drop corrupt entries (< 500 chars — real
      // 1600px JPEGs at 0.85 quality are 40-200 KB base64). Prevents
      // an iOS-Safari canvas-silent-failure "data:," ghost payload
      // from persisting to IDB and later 400ing the drain worker.
      // Observations differ from arch here: an observation may
      // legitimately be text-only, so an EMPTY photos array is fine.
      // Only the individual entry integrity is enforced.
      photos_base64: Array.isArray(observationPayload.photos_base64)
        ? observationPayload.photos_base64.filter(
            (b) => typeof b === 'string' && b.length > 500,
          )
        : [],
      queued_at: new Date().toISOString(),
      status: 'pending',
      retry_count: 0,
      last_error: null,
    };
    const req = store.add(record);
    req.onsuccess = () => resolve({ id: req.result, offline_observation_key: record.offline_observation_key });
    req.onerror = () => reject(req.error);
  });
};

export const getPendingObservations = async () => {
  const db = await initObservationDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readonly');
    const store = tx.objectStore(OPS_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const all = req.result || [];
      resolve(all.filter(r => r.status === 'pending' || r.status === 'failed'));
    };
    req.onerror = () => reject(req.error);
  });
};

export const getPendingObservationCount = async () => {
  try {
    const ops = await getPendingObservations();
    return ops.length;
  } catch {
    return 0;
  }
};

export const updateObservationOpStatus = async (id, status, errorMessage = null) => {
  const db = await initObservationDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (!record) {
        reject(new Error(`Observation op ${id} not found`));
        return;
      }
      record.status = status;
      record.last_updated = new Date().toISOString();
      if (errorMessage) {
        record.last_error = errorMessage;
        record.retry_count = (record.retry_count || 0) + 1;
      }
      if (status === 'completed') {
        record.completed_at = new Date().toISOString();
      }
      const putReq = store.put(record);
      putReq.onsuccess = () => resolve(record);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

export const clearCompletedObservations = async () => {
  const db = await initObservationDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const completed = (req.result || []).filter(r => r.status === 'completed');
      let deleted = 0;
      completed.forEach(record => {
        store.delete(record.id);
        deleted++;
      });
      resolve(deleted);
    };
    req.onerror = () => reject(req.error);
  });
};

export const clearAllObservationOps = async () => {
  const db = await initObservationDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    tx.objectStore(OPS_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};
